import { getPointer } from "libs/schema";
import { toast } from "~/environment/toast-service";
import { withActionContext, write } from "./write";
import { InvalidActionStateError } from "libs/actions";
import * as actions from "libs/actions";
import { getLastSentOrScheduledMessageInThreadDetails } from "~/queries/getLastSentOrScheduledMessageInThreadDetails";
import { isDefined } from "libs/predicates";
import { DeferredPromise, promiseAllKeyed } from "libs/promise-utils";
import { ClientEnvironment } from "~/environment/ClientEnvironment";

/* -------------------------------------------------------------------------------------------------
 *  triageThread
 * -------------------------------------------------------------------------------------------------
 */

export type TriageThreadProps = {
  threadId: string | string[];
  done?: boolean;
  resolveRequests?: true;
  remindAt?: string | null;
  isStarred?: boolean;
  noToast?: boolean;
  noUndo?: boolean;
  onOptimisticUndo?: () => void;
};

/**
 * Triages a thread by updating the associated inbox notification.
 * If no inbox notification currently exists for this thread, one will
 * be created using the last post in the thread. When marking the
 * thread as "done", we will also record that the user has read up
 * to the current last post in the thread.
 */
export const triageThread = withActionContext(async (context, environment, props: TriageThreadProps) => {
  const threadIds = Array.isArray(props.threadId) ? props.threadId : [props.threadId];

  const dependencies = await Promise.all(
    threadIds.map(async (threadId) => {
      const {
        notificationResult: [notification],
        readReceiptResult: [threadReadReceipt],
        threadRequestsResult: [currentUserThreadRequests],
        lastMessage,
      } = await promiseAllKeyed({
        notificationResult: context.environment.loader.getRecord(
          getPointer("notification", { user_id: context.currentUserId, thread_id: threadId }),
        ),
        readReceiptResult: context.environment.loader.getRecord(
          getPointer("thread_read_receipt", { user_id: context.currentUserId, thread_id: threadId }),
        ),
        threadRequestsResult: context.environment.loader.getThreadRequests({
          thread_id: threadId,
          recipient_user_id: context.currentUserId,
        }),
        lastMessage: getLastSentOrScheduledMessageInThreadDetails(environment, {
          threadId,
          getThreadFetchStrategy: "cache-first",
          getMessagesScheduledToBeSentFetchStrategy: "cache",
        }),
      });

      return {
        threadId,
        notification,
        threadReadReceipt,
        currentUserThreadRequests,
        lastMessage,
      };
    }),
  );

  const deferredWasReadReceiptUpdated = new DeferredPromise<boolean>();

  const reindexNotifications = () => {
    const hasReindexableChange =
      isDefined(props.isStarred) || isDefined(props.remindAt) || isDefined(props.resolveRequests);

    if (!hasReindexableChange) return;

    threadIds.forEach((threadId) => {
      reindexNotification(environment, { threadId });
    });
  };

  return {
    forward: async () => {
      try {
        const action = await actions.notification.triageThreads.action(
          {
            threads: dependencies.map((deps): actions.notification.TriageThreadData => {
              return {
                threadId: deps.threadId,
                lastMessage:
                  deps.lastMessage ?
                    {
                      id: deps.lastMessage.id,
                      timelineOrder: deps.lastMessage.timeline_order,
                      sentAt: deps.lastMessage.sent_at,
                    }
                  : null,
                expectedNotificationMessageId: deps.notification?.message_id ?? null,
                remindAt: props.remindAt,
                done: props.done,
                isStarred: props.isStarred,
                resolveRequests: props.resolveRequests,
              };
            }),
            timestamp: new Date(),
          },
          context,
        );

        const wasReadReceiptUpdated = action.operations.some((op) => op.table === "thread_read_receipt");

        deferredWasReadReceiptUpdated.resolve(wasReadReceiptUpdated);

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            reindexNotifications();

            if (props.noToast) return;

            toast("vanilla", {
              subject: getToastSubject(environment, { params: props }),
              durationMs: 5000,
            });
          },
        });
      } catch (error) {
        if (error instanceof InvalidActionStateError) {
          error.userMessage = getToastSubject(environment, { params: props, error });
        }

        throw error;
      }
    },
    backward: async () => {
      try {
        const wereReceiptsUpdated = await deferredWasReadReceiptUpdated.promise;

        const action = await actions.notification.undoTriageThreads.action(
          {
            threads: dependencies.map((deps): actions.notification.UndoTriageThreadData => {
              const { threadId, notification, lastMessage, threadReadReceipt, currentUserThreadRequests } = deps;

              // If the notification didn't already exist when we performed the triage action then a new one would
              // have been created using the last message ID as the notification's message_id value.
              const expectedNotificationMessageId = notification?.message_id ?? lastMessage?.id;

              if (!expectedNotificationMessageId) {
                // If this is falsey then there is no notification associated with this threadId (so the forward
                // action for this threadId failed).
                throw new actions.InvalidActionStateError(
                  "[undoTriageThreads] expectedNotificationMessageId is falsey",
                  { errorData: { notification, deps } },
                );
              }

              let receiptValues: actions.notification.UndoTriageThreadData["receiptValues"];

              if (wereReceiptsUpdated) {
                if (notification && threadReadReceipt) {
                  receiptValues = {
                    upToTimelineId: threadReadReceipt.read_to_timeline_id,
                    upToTimelineOrder: threadReadReceipt.read_to_timeline_order,
                  };
                } else {
                  receiptValues = null;
                }
              }

              return {
                threadId,
                expectedNotificationMessageId,
                notification,
                currentUserThreadRequests: props.resolveRequests ? currentUserThreadRequests : undefined,
                receiptValues,
              };
            }),
          },
          context,
        );

        await write(environment, {
          action,
          onOptimisticWrite: () => {
            reindexNotifications();
            props.onOptimisticUndo?.();

            if (props.noToast) return;

            toast("vanilla", {
              subject: getToastSubject(environment, { params: props, isUndo: true }),
              durationMs: 5000,
            });
          },
        });
      } catch (error) {
        if (error instanceof InvalidActionStateError) {
          error.userMessage = getToastSubject(environment, { params: props, error, isUndo: true });
        }

        throw error;
      }
    },
  };
});

/**
 * Returns an observable that, when subscribed to, ensures the cache is warmed for snappy optimistic
 * resolution of triageThread actions.
 */
export function observeTriageThreadDeps(
  environment: Pick<ClientEnvironment, "recordLoader" | "auth">,
  props: {
    threadId: string;
  },
) {
  return environment.recordLoader.observeGetThreadRequests(
    {
      thread_id: props.threadId,
      recipient_user_id: environment.auth.getCurrentUserId(),
    },
    { fetchStrategy: "cache-first" },
  );
}

/* -------------------------------------------------------------------------------------------------
 *  removeNotificationForPermissionDeniedThread
 * -------------------------------------------------------------------------------------------------
 */

export const removeNotificationForPermissionDeniedThread = withActionContext(
  async (context, environment, props: { threadId: string }) => {
    return {
      forward: async () => {
        const action = await actions.notification.deleteNotification.action(props, context);

        await write(environment, { action });
      },
    };
  },
);

/* -------------------------------------------------------------------------------------------------
 *  reindexNotification
 * -------------------------------------------------------------------------------------------------
 */

export const reindexNotification = withActionContext(async (context, environment, props: { threadId: string }) => {
  return {
    forward: async () => {
      const pointer = getPointer("notification", { user_id: context.currentUserId, thread_id: props.threadId });

      const [notification] = await context.environment.loader.getRecord(pointer);

      if (!notification) {
        // No need to reindex if the notification doesn't exist
        environment.logger.debug(
          { id: pointer.id, threadId: props.threadId },
          "[reindexNotification] Notification not found",
        );

        return;
      }

      const action = await actions.notification.reindexNotification.action(props, context);

      await write(environment, { action });
    },
  };
});

/* -----------------------------------------------------------------------------------------------*/

function getToastSubject(
  environment: Pick<ClientEnvironment, "logger">,
  props: { params: TriageThreadProps; error?: unknown; isUndo?: boolean },
) {
  const { params, error, isUndo } = props;

  const count = Array.isArray(params.threadId) ? params.threadId.length : 1;
  let subject: string;

  // Note that we will often update multiple properties when triaging a thread,
  // so the order of these checks is important. The current messages make sense
  // for the current ways we triage threads, but in the future we might need
  // some additional branches.
  if (isDefined(params.remindAt)) {
    if (error) {
      subject =
        params.remindAt ?
          `Error setting ${count} reminder${count === 1 ? "" : "s"}.`
        : `Error removing ${count} reminder${count === 1 ? "" : "s"}.`;
    } else {
      subject =
        params.remindAt ?
          `${count} reminder${count === 1 ? "" : "s"} set.`
        : `${count} reminder${count === 1 ? "" : "s"} removed.`;
    }
  } else if (isDefined(params.isStarred)) {
    if (error) {
      subject =
        params.isStarred ?
          `Error starring ${count} thread${count === 1 ? "" : "s"}.`
        : `Error unstarring ${count} thread${count === 1 ? "" : "s"}.`;
    } else {
      subject =
        params.isStarred ?
          `Starred ${count} thread${count === 1 ? "" : "s"}.`
        : `Unstarred ${count} thread${count === 1 ? "" : "s"}.`;
    }
  } else if (isDefined(params.done)) {
    if (error) {
      subject =
        params.done ?
          `Error marking ${count} thread${count === 1 ? "" : "s"} done.`
        : `Error marking ${count} thread${count === 1 ? "" : "s"} not done.`;
    } else {
      subject =
        params.done ?
          `${count} thread${count === 1 ? "" : "s"} marked done.`
        : `${count} thread${count === 1 ? "" : "s"} marked not done.`;
    }
  } else if (isDefined(params.resolveRequests)) {
    if (error) {
      subject =
        params.resolveRequests ?
          `Error marking ${count} thread${count === 1 ? "" : "s"} resolved.`
        : `Error marking ${count} thread${count === 1 ? "" : "s"} not resolved.`;
    } else {
      subject =
        params.resolveRequests ?
          `${count} thread${count === 1 ? "" : "s"} resolved.`
        : `${count} thread${count === 1 ? "" : "s"} not resolved.`;
    }
  } else {
    environment.logger.error({ params }, "[triageThreadToast] unexpected params");
    return InvalidActionStateError.defaultUserMessage;
  }

  if (isUndo) {
    subject = `Undoing: ${subject}`;
  }

  return subject;
}
